import { throttle, throwTranslatableError } from '@/core';
import rdbClient from 'rdb-client';
import {
	getFilteredTripAddressesRequest, getAllTripAddressesRequest, newTripAddressRequest, getGlobalPostalPlaceRequest, updateTripAddressRequest, deleteTripAddressRequest, getLocationsRequest
} from '../requests';

async function fetchFilteredTripAddresses({}, filter) {
	let response = [];
	try {
		response = await getFilteredTripAddressesRequest(filter);
	} catch (err) {
		throw new Error('failedToFetch');
	}
	return response;
}

async function fetchAllTripAddresses({ state, commit }) {
	commit('set', (state) => { state.loading = true; });
	try {
		if (state.allTripAddresses.length === 0) {
			await fetchAllTripAddressesCore();
		} else {
			await throttle(fetchAllTripAddressesCore, 'fetchAllTripAddresses', 60 * 1000);
		}
	} catch (err) {
		commit('set', (state) => { state.loading = false; });
		throw new Error('failedToFetch');
	}
	commit('set', (state) => { state.loading = false; });

	async function fetchAllTripAddressesCore() {
		let response = await getAllTripAddressesRequest();
		commit('setAllTripAddresses', response);
	}
}

async function addNewTripAddress({ commit }, newLocation ) {
	let response = newLocation;
	try {
		response = await newTripAddressRequest(newLocation);
		commit('clearErrors');
	} catch (e) {
		if (e.response && e.response.data && e.response.data.errors) {
			commit('setErrors', e.response.data.errors);
		}
		throwTranslatableError(e, 'failedToSave');
	}
	return response;
}

async function getGlobalPostalPlace({}, { postalCode, countryCode }) {
	let response = {};
	try {
		response = await getGlobalPostalPlaceRequest({ postalCode, countryCode });
	} catch (err) {
		throw new Error('failedToFetch');
	}
	return response;
}

async function addEmptyTripAddress({ state, commit } ) {
	let address = {
		id: null,
		name: '',
		street: '',
		postalCode: '',
		postalPlace: '',
		countryCode: 'NO',
		latitude: null,
		longitude: null,
		linkedLocations: []
	};
	try {
		let response = await newTripAddressRequest(address);
		address.id = response.id;
		commit('clearErrors');
		let { allTripAddresses } = state;
		allTripAddresses.unshift(address);
		commit('set', () => { state.allTripAddresses = allTripAddresses; });
	} catch (e) {
		if (e.response && e.response.data && e.response.data.errors) {
			commit('setErrors', e.response.data.errors);
		}
		throwTranslatableError(e, 'failedToAdd');
	}
	return address;
}

async function setTripAddress({ commit }, { tripAddress, data, prop }) {
	if (tripAddress[prop] === data) return;

	let tripAddressClone = JSON.parse(JSON.stringify(tripAddress));
	commit('set', () => { tripAddress[prop] = data; });
	let patch = rdbClient.createPatch(tripAddressClone, tripAddress);
	try {
		await updateTripAddressRequest(tripAddress.id, { patch });
		commit('clearErrors');
	} catch (err) {
		if (err.response) {
			commit('setErrors', { errors: err.response.data.errors, id: tripAddress.id });
		}
		commit('set', () => { tripAddress[prop] = tripAddressClone[prop]; });
		throw (err);
	}
}

async function deleteTripAddresses({ state, commit }, tripAddressesToDelete) {
	let storedTripAddresses = state.allTripAddresses;
	let idsToRemove = [];
	for (let address of tripAddressesToDelete) {
		idsToRemove.push(address.id);
	}
	try {
		for (let id of idsToRemove) {
			await deleteTripAddressRequest(id);
		}
		let updatedTripAddresses = storedTripAddresses.filter(x => idsToRemove.indexOf(x.id) < 0);
		commit('set', () => { state.allTripAddresses = updatedTripAddresses; });
	} catch (err) {
		throw new Error('failedToDelete');
	}
}

async function fetchAllGlobalLocations({ commit }) {
	try {
		let response = await getLocationsRequest();
		commit('setGlobalLocations', response);
	} catch (err) {
		throw new Error('failedToFetch');
	}
}

async function updateLinkedLocations({ state, commit }, { tripAddressId, data }) {
	let tripAddress = state.allTripAddresses.find(x => x.id === tripAddressId);
	let tripAddressClone = JSON.parse(JSON.stringify(tripAddress));
	commit('set', () => {
		tripAddress.linkedLocations = data;
	});
	let patch = rdbClient.createPatch(tripAddressClone, tripAddress);
	try {
		await updateTripAddressRequest(tripAddress.id, { patch });
		commit('clearErrors');
	} catch (err) {
		console.log(err);
		if (err.response) {
			commit('setErrors', { errors: err.response.data.errors, id: tripAddress.id });
		}
		commit('set', () => { tripAddress.linkedLocations = tripAddressClone.linkedLocations; });
		throw (err);
	}
}

export default {
	fetchFilteredTripAddresses,
	fetchAllTripAddresses,
	addNewTripAddress,
	getGlobalPostalPlace,
	addEmptyTripAddress,
	setTripAddress,
	deleteTripAddresses,
	fetchAllGlobalLocations,
	updateLinkedLocations
};
